import { useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  X,
  Save,
  Trash2,
  Users,
  KeyRound,
  Check,
  Ban,
  Search,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  useEquipo,
  useRoles,
  useCreateMiembro,
  useUpdateMiembro,
  useResetMiembroPassword,
  useDeleteMiembro,
  type MiembroEquipo,
} from "../api";

type ModalMode = "crear" | "editar" | "password" | null;

export function EquipoPage() {
  const { data: miembros, isLoading } = useEquipo();
  const { data: roles } = useRoles();
  const createMut = useCreateMiembro();
  const updateMut = useUpdateMiembro();
  const resetMut = useResetMiembroPassword();
  const deleteMut = useDeleteMiembro();

  const [mode, setMode] = useState<ModalMode>(null);
  const [editing, setEditing] = useState<MiembroEquipo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState("");

  const rolesDisponibles = useMemo(
    () => (roles ?? []).map((r) => r.name).filter((n) => n !== "super-admin"),
    [roles],
  );

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return miembros ?? [];
    return (miembros ?? []).filter(
      (m) =>
        m.nombre.toLowerCase().includes(q) ||
        m.correo.toLowerCase().includes(q) ||
        m.rol.toLowerCase().includes(q),
    );
  }, [miembros, busca]);

  function openCrear() {
    setEditing(null);
    setNombre("");
    setCorreo("");
    setPassword("");
    setRol(rolesDisponibles[0] ?? "empleado");
    setError(null);
    setMode("crear");
  }
  function openEditar(m: MiembroEquipo) {
    setEditing(m);
    setNombre(m.nombre);
    setCorreo(m.correo);
    setRol(m.rol || m.roles[0] || "empleado");
    setError(null);
    setMode("editar");
  }
  function openPassword(m: MiembroEquipo) {
    setEditing(m);
    setPassword("");
    setError(null);
    setMode("password");
  }
  function close() {
    setMode(null);
    setEditing(null);
    setError(null);
  }

  function errMsg(err: unknown, fallback: string): string {
    const e = err as {
      response?: { data?: { message?: string; errors?: Record<string, string[]> } };
    };
    const first = e?.response?.data?.errors
      ? Object.values(e.response.data.errors).flat()[0]
      : null;
    return first ?? e?.response?.data?.message ?? fallback;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "crear") {
        await createMut.mutateAsync({ nombre, correo, password, rol });
      } else if (mode === "editar" && editing) {
        await updateMut.mutateAsync({ id: editing.id, payload: { nombre, correo, rol } });
      } else if (mode === "password" && editing) {
        await resetMut.mutateAsync({ id: editing.id, password });
      }
      close();
    } catch (err) {
      setError(errMsg(err, "No se pudo guardar."));
    }
  }

  async function toggleActivo(m: MiembroEquipo) {
    try {
      await updateMut.mutateAsync({ id: m.id, payload: { activo: !m.activo } });
    } catch (err) {
      alert(errMsg(err, "No se pudo cambiar el estado."));
    }
  }

  async function eliminar(m: MiembroEquipo) {
    if (!confirm(`¿Eliminar la cuenta de "${m.nombre}"? Esta acción no se puede deshacer.`))
      return;
    try {
      await deleteMut.mutateAsync(m.id);
    } catch (err) {
      alert(errMsg(err, "No se pudo eliminar."));
    }
  }

  const saving = createMut.isPending || updateMut.isPending || resetMut.isPending;

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Users size={28} className="text-brand-700" /> Equipo
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona las cuentas de tu equipo: alta, rol, acceso y contraseñas.
          </p>
        </div>
        <button
          onClick={openCrear}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
        >
          <Plus size={18} /> Nuevo usuario
        </button>
      </header>

      <div className="relative shrink-0 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nombre, correo o rol…"
          className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm"
        />
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {miembros && (
        <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-soft border border-slate-200/70">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Usuario</th>
                <th className="text-left px-4 py-3 font-bold">Rol</th>
                <th className="text-center px-4 py-3 font-bold">Estado</th>
                <th className="text-right px-4 py-3 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900">{m.nombre}</div>
                    <div className="text-xs text-slate-500">{m.correo}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-brand-100 text-brand-700">
                      {m.rol || m.roles[0] || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleActivo(m)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition ${
                        m.activo
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"
                      }`}
                      title={m.activo ? "Clic para desactivar" : "Clic para activar"}
                    >
                      {m.activo ? <Check size={11} /> : <Ban size={11} />}
                      {m.activo ? "Activo" : "Inactivo"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => openEditar(m)}
                        className="rounded-lg border border-slate-200 hover:bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => openPassword(m)}
                        className="rounded-lg border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 text-slate-500"
                        title="Resetear contraseña"
                      >
                        <KeyRound size={15} />
                      </button>
                      <button
                        onClick={() => eliminar(m)}
                        className="rounded-lg border border-slate-200 hover:bg-rose-50 hover:text-rose-600 px-2.5 py-1.5 text-slate-400"
                        title="Eliminar"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No hay usuarios que coincidan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {mode && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {mode === "crear" && "Nuevo usuario"}
                {mode === "editar" && `Editar: ${editing?.nombre}`}
                {mode === "password" && `Contraseña de ${editing?.nombre}`}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>

            {error && (
              <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
                {error}
              </div>
            )}

            {mode !== "password" && (
              <>
                <div className="mb-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Nombre *
                  </label>
                  <input
                    required
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Correo *
                  </label>
                  <input
                    required
                    type="email"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Rol *
                  </label>
                  <select
                    value={rol}
                    onChange={(e) => setRol(e.target.value)}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {rolesDisponibles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {(mode === "crear" || mode === "password") && (
              <div className="mb-3">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Contraseña {mode === "crear" ? "*" : "nueva *"}
                </label>
                <div className="flex gap-1 mt-1">
                  <input
                    required
                    minLength={8}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                    title={showPassword ? "Ocultar" : "Mostrar"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
